import React, { useMemo, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { StyleSheet, View, ScrollView, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useRouter } from 'expo-router';
import ImageStyleCard from '@/components/ImageStyleCard';
import CreateYourOwnStyleCard from '@/components/CreateYourOwnStyleCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useChatInputStore } from '@/store';
import { VIDEO_STYLES } from '@/constants/config';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    position: 'relative',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: isXS ? 12 : 24,
    justifyContent: 'space-between',
  },
  gridItem: {
    width: '31%',
    minWidth: 0,
    marginBottom: 6,
  },
  scrollView: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  scrollContent: {
    paddingRight: isXS ? 16 : 24,
    paddingLeft: Platform.OS === 'web' ? 0 : isXS ? 16 : 24,
  },
});

const VideoStyleCarousel = forwardRef(function VideoStyleCarousel({ onScrollStateChange }, ref) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  
  const initializeVideoGeneration = useChatInputStore(state => state.initializeVideoGeneration);
  const requireAuth = useRequireAuth();

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < contentWidth - containerWidth - 10;
  const showNavButtons = ['web', 'macos'].includes(Platform.OS) && !isXS && contentWidth > containerWidth;

  useImperativeHandle(ref, () => ({
    scrollBy(amount) {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          x: scrollPosition + amount,
          animated: true,
        });
      }
    },
  }), [scrollPosition]);

  const handleScroll = useCallback((event) => {
    setScrollPosition(event.nativeEvent.contentOffset.x);
  }, []);

  const handleContentSizeChange = useCallback((width) => {
    setContentWidth(width);
  }, []);

  const handleLayout = useCallback((event) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const handleStylePress = useCallback((styleData) => {
    if (!styleData?.promptTemplate) return;
    // Gate before pushing to `/app` with a `pendingAutoSend` (auto-send
    // requires auth, otherwise the prompt is silently dropped).
    const ran = requireAuth(() => true, {
      source: 'videos_style_carousel',
      title: 'Sign in to make a video',
      subtitle: 'Video generations are billed to your account so we can apply your credits and save the result.',
      icon: 'video',
    });
    if (ran !== true) return;
    initializeVideoGeneration(styleData.promptTemplate, null, true);
    router.push('/app');
  }, [initializeVideoGeneration, router, requireAuth]);

  const handleCreateYourOwn = useCallback(() => {
    router.push('/app');
  }, [router]);

  const lastReported = useRef({ canScrollLeft: false, canScrollRight: false, showNavButtons: false });
  const reportState = useCallback(() => {
    const next = {
      canScrollLeft: isXS ? false : canScrollLeft,
      canScrollRight: isXS ? false : canScrollRight,
      showNavButtons: isXS ? false : showNavButtons,
    };
    if (
      lastReported.current.canScrollLeft !== next.canScrollLeft ||
      lastReported.current.canScrollRight !== next.canScrollRight ||
      lastReported.current.showNavButtons !== next.showNavButtons
    ) {
      lastReported.current = next;
      onScrollStateChange?.(next);
    }
  }, [isXS, canScrollLeft, canScrollRight, showNavButtons, onScrollStateChange]);
  React.useEffect(reportState, [reportState]);

  if (isXS) {
    return (
      <View style={styles.container}>
        <View style={styles.grid}>
          {VIDEO_STYLES.map((styleData) => (
            <View key={styleData.id} style={styles.gridItem}>
              <ImageStyleCard
                style={styleData}
                onPress={handleStylePress}
                compact
              />
            </View>
          ))}
          <View style={styles.gridItem}>
            <CreateYourOwnStyleCard onPress={handleCreateYourOwn} compact />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} onLayout={handleLayout}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
      >
        {VIDEO_STYLES.map((styleData) => (
          <ImageStyleCard
            key={styleData.id}
            style={styleData}
            onPress={handleStylePress}
          />
        ))}
        <CreateYourOwnStyleCard onPress={handleCreateYourOwn} />
      </ScrollView>
    </View>
  );
});

export default React.memo(VideoStyleCarousel);
